import { Component, Event, EventEmitter, h, Host, JSX, State } from '@stencil/core';
import { MESSAGES } from '../constants';
import { store } from '../../utils/store';
import {
  FeedbackSubmitDocument,
  FeedbackSubmitMutation,
  FeedbackSubmitMutationVariables
} from '../../generated/operations';
import { BelimoError } from '../app-error-messages/type';

const ITEMS = Array.from({ length: 5 }, (_, k) => k + 1);

@Component({
    tag: 'feedback-form',
    shadow: false,
})
export class FeedbackForm {
    @State() isNotificationVisible = false;
    @Event() belimoError?: EventEmitter<BelimoError>;
    @State() inProgress = false;
    @State() notValid = true;
    @State() showReasons = false;
    private form!: HTMLFormElement;

    public onSubmit(e: any): void {
        e.preventDefault();
        this.inProgress = true
        const f = new FormData(e.target);
        const reasons = this.getReasons()
        .map((reason) => {
            switch (reason) {
                case '1':
                    return store.state.config.i18n.FEEDBACK_FORM_REASON_1;
                case '2':
                    return store.state.config.i18n.FEEDBACK_FORM_REASON_2;
                case '3':
                    return store.state.config.i18n.FEEDBACK_FORM_REASON_3;
                case '4':
                    return store.state.config.i18n.FEEDBACK_FORM_REASON_4;
                default:
                    return '';
            }
        });

        const description = f.get('description').toString();
        store.state.apollo.mutate<FeedbackSubmitMutation, FeedbackSubmitMutationVariables>({
            mutation: FeedbackSubmitDocument,
            variables: {
                form: {
                    region: store.state.currentRegion,
                    country: store.state.currentCountry,
                    rating: f.get('rating') ? parseInt(f.get('rating').toString(), 10) : 0,
                    description: [
                        ...(reasons.length > 0 ? [
                            reasons.join('<br />\n'),
                        ] : []),
                        ...(description.length > 0 ? [
                            '',
                            '<strong>Additional comment</strong>:',
                            description,
                        ] : [])
                    ].join('<br />\n')
                }
            }
        }).then(() => {
            this.inProgress = false;
            store.state.isFeedbackPopupVisible = false;
            this.isNotificationVisible = true;
        }).catch(() => {
            this.inProgress = false;
            this.belimoError?.emit({ type: 'unknown', cause: e.message });
        })
    }

    public close(): void {
        store.state.isFeedbackPopupVisible = false;
    }

    private getReasons() {
        const f = new FormData(this.form);
        return f.getAll('reason');
    }

    private handleCheckbox() {
      this.notValid = this.showReasons && this.getReasons().length === 0;
    }

    public render(): JSX.Element {
        const placeholder = store.state.config
            && store.state.config.i18n.FEEDBACK_FORM_DESCRIPTION_PLACEHOLDER || '';
        return (
            <Host>
                {this.isNotificationVisible && <app-notification onBelimoNotificationClose={() => {
                    this.isNotificationVisible = false;
                }}>
                    <span slot="header"><belimo-i18n messageId={MESSAGES.FEEDBACK_FORM_SUCCESS_TEXT}/></span>
                </app-notification>}
                {store.state.isFeedbackPopupVisible && <form
                    ref={(el) => this.form = el}
                    class={{
                        popup: true,
                        'popup--feedback': true,
                        'popup--feedback-long': this.showReasons
                    }} onSubmit={(e) => this.onSubmit(e)}>
                    <div class="popup__overlay"/>
                    <div class="popup__window">
                        {this.inProgress && <div class="popup__cover">
                            <div class="loader"/>
                        </div>}
                        <div class="popup__close" onClick={() => this.close()}>
                            <belimo-svg-sprite svgId={'x'}/>
                        </div>
                        <div class="popup__header">
                            <div class="popup__header-icon">
                                <belimo-svg-sprite svgId={'support'}/>
                            </div>
                            <h1 class="popup__header-title">
                                <belimo-i18n messageId={MESSAGES.FEEDBACK_FORM_TITLE}/>
                            </h1>
                        </div>
                        <div class="popup__content">
                            <h4 class="title">
                                <belimo-i18n messageId={MESSAGES.FEEDBACK_FORM_HEADER}/>
                            </h4>
                            <div class="form-group">
                                <div class="form-group__control">
                                    <div class="rating">
                                        {ITEMS.map((item) => (
                                            <input type="radio" name="rating" class="rating__control screen-reader"
                                                   onChange={() => {
                                                       this.showReasons = item < 4;
                                                       this.notValid = item < 4;
                                                   }}
                                                   value={item} id={`rc${item}`}/>
                                        ))}
                                        {ITEMS.map((item) => (
                                            <label htmlFor={`rc${item}`} class="rating__item">
                                                <belimo-svg-sprite class="rating__star" svgId={'star'}/>
                                                <span>{item}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {this.showReasons &&
                                <div class="problem-list-checkboxes">
                                    <div class="checkbox">
                                        <label class="checkbox__wrapper">
                                            <input class="checkbox__value" name="reason" type="checkbox" value="1"
                                                   onChange={() => { this.handleCheckbox() }}/>
                                            <span class="checkbox__icon"></span>
                                            <span class="checkbox__text"><belimo-i18n
                                                messageId={MESSAGES.FEEDBACK_FORM_REASON_1}/></span>
                                        </label>
                                    </div>

                                    <div class="checkbox">
                                        <label class="checkbox__wrapper">
                                            <input class="checkbox__value" name="reason" type="checkbox" value="2"
                                                   onChange={() => { this.handleCheckbox() }}/>
                                            <span class="checkbox__icon"></span>
                                            <span class="checkbox__text"><belimo-i18n
                                                messageId={MESSAGES.FEEDBACK_FORM_REASON_2}/></span>
                                        </label>
                                    </div>

                                    <div class="checkbox">
                                        <label class="checkbox__wrapper">
                                            <input class="checkbox__value" name="reason" type="checkbox" value="3"
                                                   onChange={() => { this.handleCheckbox() }}/>
                                            <span class="checkbox__icon"></span>
                                            <span class="checkbox__text"><belimo-i18n
                                                messageId={MESSAGES.FEEDBACK_FORM_REASON_3}/></span>
                                        </label>
                                    </div>

                                    <div class="checkbox">
                                        <label class="checkbox__wrapper">
                                            <input class="checkbox__value" name="reason" type="checkbox" value="4"
                                                   onChange={() => { this.handleCheckbox() }}/>
                                            <span class="checkbox__icon"></span>
                                            <span class="checkbox__text"><belimo-i18n
                                                messageId={MESSAGES.FEEDBACK_FORM_REASON_4}/></span>
                                        </label>
                                    </div>

                                </div>}
                            <h4>
                                <belimo-i18n messageId={MESSAGES.FEEDBACK_FORM_DESCRIPTION}/>
                            </h4>
                            <div class="form-group">
                                <div class="form-group__control"><textarea
                                    name="description" class="textArea"
                                    placeholder={placeholder}
                                />
                                </div>
                            </div>
                        </div>
                        <div class="popup__footer">
                            <button type="reset" class="btn btn--secondary" onClick={() => this.close()}>
                                <belimo-i18n messageId={MESSAGES.CANCEL}/>
                            </button>
                            <button disabled={this.inProgress || this.notValid} type="submit" class="btn btn--primary">
                                <belimo-i18n messageId={MESSAGES.SAVE}/>
                            </button>
                        </div>
                    </div>
                </form>}
            </Host>
        )
    }
}
